import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModelInfo, UseModelDownloadReturn } from '../types';
import { TrainingApiService } from '../../../services/trainingApiService';

/**
 * Custom hook for ModelDownloadModal state management and API operations
 */
export const useModelDownload = (
  sessionId: string,
  isOpen: boolean,
  onClose: () => void,
  onModelSaved?: () => void
): UseModelDownloadReturn => {
  const { t } = useTranslation('training');

  const [models, setModels] = useState<ModelInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load models from API
  const loadModels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await TrainingApiService.listModelsDatabase(sessionId);
      if (response.success && response.data?.models) {
        setModels(response.data.models);
      } else {
        setError(t('training.modelDownload.noModelsAvailable'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('training.modelDownload.loadError'));
    } finally {
      setLoading(false);
    }
  }, [sessionId, t]);

  // Load models when modal opens
  useEffect(() => {
    if (isOpen && sessionId) {
      loadModels();
    }
  }, [isOpen, sessionId, loadModels]);

  // Download single model
  const handleDownload = useCallback(async (model: ModelInfo) => {
    setDownloading(model.filename);
    try {
      await TrainingApiService.downloadModelH5(sessionId, model.filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('training.modelDownload.downloadError'));
    } finally {
      setDownloading(null);
    }
  }, [sessionId, t]);

  // Download all models sequentially
  const handleDownloadAll = useCallback(async () => {
    if (models.length === 0) return;

    setDownloadingAll(true);
    setError(null);

    try {
      // Download all models sequentially
      for (const model of models) {
        await TrainingApiService.downloadModelH5(sessionId, model.filename);
        // Small delay between downloads to avoid overwhelming the browser
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('training.modelDownload.downloadError'));
    } finally {
      setDownloading(null);
      setDownloadingAll(false);
    }
  }, [models, sessionId, t]);

  // Handle save to database (close modal)
  const handleSaveToDatabase = useCallback(() => {
    onModelSaved?.();
    onClose();
  }, [onModelSaved, onClose]);

  return {
    models,
    loading,
    downloading,
    downloadingAll,
    error,
    handleDownload,
    handleDownloadAll,
    handleSaveToDatabase
  };
};
